/**
 * Custom hooks for fetching orders and related data from Firestore
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  where,
  onSnapshot,
  QueryConstraint,
  doc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { Order, LogEntry, QueueStatus, OrderStatus, BotStatus } from '../types';

/**
 * Real-time listener for orders
 */
export function useOrders(statusFilter?: OrderStatus, limitCount = 50) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const constraints: QueryConstraint[] = [];

    if (statusFilter) {
      constraints.push(where('status', '==', statusFilter));
    }

    constraints.push(orderBy('created_at', 'desc'));
    constraints.push(limit(limitCount));

    const q = query(collection(db, 'orders'), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ordersData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Order[];

        setOrders(ordersData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching orders:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [statusFilter, limitCount]);

  return { orders, loading, error };
}

/**
 * Fetches a single order by ID
 */
export function useOrder(orderId: string | undefined) {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    const orderRef = doc(db, 'orders', orderId);

    const unsubscribe = onSnapshot(
      orderRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setOrder({ id: snapshot.id, ...snapshot.data() } as Order);
        } else {
          setOrder(null);
          setError('Order not found');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching order:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orderId]);

  return { order, loading, error };
}

/**
 * Real-time listener for logs of a specific order
 */
export function useOrderLogs(orderId: string | undefined) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'logs'),
      where('order_id', '==', orderId),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const logsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as LogEntry[];

        setLogs(logsData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching logs:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orderId]);

  return { logs, loading, error };
}

/**
 * Real-time listener for queue status
 */
export function useQueueStatus() {
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const queueRef = doc(db, 'queue', 'current');

    const unsubscribe = onSnapshot(
      queueRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setQueueStatus({ id: snapshot.id, ...snapshot.data() } as QueueStatus);
        } else {
          setQueueStatus(null);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching queue status:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { queueStatus, loading, error };
}

/**
 * Gets statistics about orders
 */
export function useOrderStats() {
  const { orders: allOrders } = useOrders(undefined, 1000);

  const stats = {
    total: allOrders.length,
    pending: allOrders.filter((o) => o.status === 'pending').length,
    processing: allOrders.filter((o) => o.status === 'processing').length,
    completed: allOrders.filter((o) => o.status === 'completed').length,
    failed: allOrders.filter((o) => o.status === 'failed').length,
    partial: allOrders.filter((o) => o.status === 'partial').length,
    orderedAwaitingPayment: allOrders.filter((o) => o.status === 'ordered_awaiting_payment').length,
    reservation: allOrders.filter((o) => o.status === 'reservation').length,
    orderedByWoocommerce: allOrders.filter((o) => o.status === 'ordered_by_woocommerce').length,
    productUnavailable: allOrders.filter((o) => o.status === 'product_unavailable').length,
  };

  return stats;
}

/**
 * Real-time listener for bot status (live preview)
 */
export function useBotStatus() {
  const [botStatus, setBotStatus] = useState<BotStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const statusRef = doc(db, 'bot_status', 'current');

    const unsubscribe = onSnapshot(
      statusRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setBotStatus({
            id: snapshot.id,
            ...snapshot.data(),
          } as BotStatus);
        } else {
          setBotStatus({
            id: 'current',
            state: 'idle',
            updated_at: new Date(),
          });
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching bot status:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { botStatus, loading, error };
}
